import { useState, useEffect, useMemo, useRef } from 'react';
import { Checkbox, Input, IconButton } from '@airbus/components-react';
import { useColumnContextAPI } from '../../../context/GetAllColumnContext';
import styles from './AllColumnsCheckbox.module.css';
import { Close, KeyboardArrowDown, KeyboardArrowUp, Search } from '@airbus/icons/react';
import { groupByAttributeGroup } from './GroupByAttributeGroup';
import { t } from '../../../../i18next';
import { useLanguageContextAPI } from '../../../context/LanguageContext';

function fuzzyOrExactMatch(input, target) {
  const pattern = input.toLowerCase();
  const str = target.toLowerCase();

  if (str.includes(pattern)) return true;

  const patternTokens = pattern.split(/\s+/).filter(Boolean);
  const strTokens = str.split(/\s+/);

  const allTokensMatch = patternTokens.every((token) =>
    strTokens.some((word) => word.includes(token)),
  );
  if (allTokensMatch) return true;

  let p = 0;
  let lastMatchIndex = -1;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === pattern[p]) {
      if (lastMatchIndex !== -1 && i - lastMatchIndex > 2) {
        return false;
      }
      lastMatchIndex = i;
      p++;
      if (p === pattern.length) return true;
    }
  }

  return false;
}

export const AllColumnsCheckbox = () => {
  const {
    selectedViewName,
    allAccessibleColumns,
    tableDetails,
    setSelectedView,
    selectedView,
    viewData,
  } = useColumnContextAPI();

  const [activeColumns, setActiveColumns] = useState([]);
  const [selected, setSelected] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [filterText, setFilterText] = useState('');
  const setIsNarrow = useState<boolean>(false)[1];
  const { language } = useLanguageContextAPI();
  const OTHER_GROUP_ID = '__other__';

  const initialViewColumnsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!selectedViewName || !Array.isArray(viewData)) {
      initialViewColumnsRef.current = [];
      return;
    }

    const matchedView = viewData.find((v) => v.viewName === selectedViewName);

    if (matchedView?.viewColumns && Array.isArray(matchedView.viewColumns)) {
      initialViewColumnsRef.current = [...matchedView.viewColumns];
    } else {
      initialViewColumnsRef.current = [];
    }
  }, [selectedViewName, viewData]);

  const groupedData = useMemo(() => {
    const filtered = allAccessibleColumns?.filter(
      (item) =>
        item.attrAvailableInGrid &&
        (item.attrManagedByAdmin || item.attrInternalName === 'id1') &&
        (item.attrInternalName !== tableDetails?.mbdDataGrid.dataGridUniqueIdentifier ||
          item.attrInternalName === 'id1'),
    );
    const rawGroups = groupByAttributeGroup(filtered || [], language);
    const validGroups = [];
    const otherChildren = [];
    rawGroups.forEach((group) => {
      const isIntId = Number.isInteger(Number(group.id));
      if (isIntId) {
        validGroups.push(group);
      } else {
        otherChildren.push(...group.children);
      }
    });
    if (otherChildren.length > 0) {
      validGroups.push({
        id: OTHER_GROUP_ID,
        label: t('Other'),
        children: otherChildren,
      });
    }
    const showAsFlat = validGroups.length === 1;
    return validGroups.map((group) => ({
      ...group,
      isGrouped:
        !showAsFlat &&
        group.children.length >= 1 &&
        (Number.isInteger(Number(group.id)) || group.id === OTHER_GROUP_ID),
    }));
  }, [allAccessibleColumns, tableDetails, language]);

  useEffect(() => {
    if (selectedView && selectedViewName) {
      if (selectedView) {
        setActiveColumns(selectedView.viewColumns);
      }
    }
  }, [selectedView, selectedViewName]);

  const filteredData = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) {
      return groupedData.map((group) => ({
        ...group,
        visibleChildren: group.children,
      }));
    }
    return groupedData
      .map((group) => {
        const matchingChildren = group.children.filter((c) =>
          fuzzyOrExactMatch(query, c.headerName),
        );
        if (matchingChildren.length > 0) {
          return {
            ...group,
            visibleChildren: matchingChildren,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [groupedData, filterText]);

  useEffect(() => {
    const initialSelected = {};
    groupedData.forEach((group) => {
      let allChecked = true;
      group.children.forEach((child) => {
        const isChecked = selectedView?.viewColumns?.includes(child.attrInternalName);
        initialSelected[child.attrInternalName] = isChecked;
        if (!isChecked) allChecked = false;
      });
      if (group.isGrouped) {
        initialSelected[group.id] = allChecked;
      }
    });
    setSelected(initialSelected);
    setActiveColumns(selectedView?.viewColumns || []);
  }, [groupedData, selectedView]);

  useEffect(() => {
    const collapsed = {};
    groupedData.forEach((group) => {
      if (group.isGrouped) collapsed[group.id] = !filterText;
    });
    setCollapsedGroups(collapsed);
  }, [groupedData, filterText]);

  const totalAll = groupedData.reduce((sum, g) => sum + g.children.length, 0);
  const selectedAll = groupedData.reduce(
    (sum, g) => sum + g.children.filter((c) => selected[c.attrInternalName]).length,
    0,
  );

  const preserveColOrder = (cols) => {
    if (!Array.isArray(cols)) return [];

    const colSet = new Set(cols);
    const order: string[] = [];

    const baseOrder = Array.isArray(initialViewColumnsRef.current)
      ? initialViewColumnsRef.current
      : [];

    if (baseOrder.length > 0) {
      baseOrder.forEach((colId) => {
        if (colSet.has(colId)) {
          order.push(colId);
          colSet.delete(colId);
        }
      });
    }

    cols.forEach((c) => {
      if (colSet.has(c)) {
        order.push(c);
        colSet.delete(c);
      }
    });

    return order;
  };

  const handleMasterToggle = (e) => {
    const checked = e.target.checked;
    const updatedSelected = {};
    const updatedActive = [];

    groupedData.forEach((group) => {
      group.children.forEach((child) => {
        updatedSelected[child.attrInternalName] = checked;
        if (checked) updatedActive.push(child.attrInternalName);
      });
      if (group.isGrouped) {
        updatedSelected[group.id] = checked;
      }
    });
    updateSelectionState(updatedSelected, preserveColOrder(checked ? updatedActive : []));
  };

  const handleChildToggle = (child, group) => (e) => {
    const checked = e.target.checked;
    const updated = { ...selected, [child.attrInternalName]: checked };

    const siblings = group.children;
    const allChecked = siblings.every((c) =>
      c.attrInternalName === child.attrInternalName ? checked : selected[c.attrInternalName],
    );
    if (group.isGrouped) updated[group.id] = allChecked;

    let updatedCols = [...activeColumns];
    if (checked) {
      if (!updatedCols.includes(child.attrInternalName)) updatedCols.push(child.attrInternalName);
    } else {
      updatedCols = updatedCols.filter((c) => c !== child.attrInternalName);
    }
    updateSelectionState(updated, preserveColOrder(updatedCols));
  };

  const updateColumnsForChild = (cols: string[], childId: string, checked: boolean) => {
    if (checked) {
      return cols.includes(childId) ? cols : [...cols, childId];
    }
    return cols.filter((c) => c !== childId);
  };

  const applyGroupSelection = (children, checked: boolean, selectedState, cols) => {
    let updatedCols = cols;

    children.forEach((child) => {
      selectedState[child.attrInternalName] = checked;
      updatedCols = updateColumnsForChild(updatedCols, child.attrInternalName, checked);
    });

    return updatedCols;
  };

  const handleParentToggle = (group) => (e) => {
    const checked = e.target.checked;

    const updated = { ...selected };

    const updatedCols = applyGroupSelection(group.children, checked, updated, activeColumns);

    updated[group.id] = checked;

    updateSelectionState(updated, preserveColOrder(updatedCols));
  };

  const updateSelectionState = (updated, orderedCols) => {
    setSelected(updated);
    setActiveColumns(orderedCols);
    setSelectedView((prev) => ({ ...prev, viewColumns: orderedCols }));
  };

  useEffect(() => {
    const setFlag = () => setIsNarrow(window.innerWidth <= 500);
    setFlag();
    window.addEventListener('resize', setFlag);
    return () => window.removeEventListener('resize', setFlag);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.searchbox}>
        <Input
          placeholder={t('search')}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          startAdornment={<Search />}
          endAdornment={
            filterText.length > 0 ? (
              <IconButton
                variant='ghost'
                size='small'
                onClick={() => setFilterText('')}
                aria-label='clear'
              >
                <Close className={styles.closeButton} />
              </IconButton>
            ) : null
          }
        />
      </div>

      <div className={styles.selectallrow}>
        <div className={styles.selectall}>
          <Checkbox
            size='medium'
            checked={selectedAll === totalAll}
            indeterminate={selectedAll > 0 && selectedAll < totalAll}
            onChange={handleMasterToggle}
            label={t('select_all')}
          />
          <span className={styles.badge}>
            {selectedAll}/{totalAll}
          </span>
        </div>
        <div
          className={styles.selectallrowcollapseexpand}
          style={{
            visibility: groupedData.some((g) => g.isGrouped) ? 'visible' : 'hidden',
          }}
        >
          <div
            className={styles.collapseall}
            onClick={() => {
              const allCollapsed = Object.values(collapsedGroups).every(Boolean);
              const newState = {};
              groupedData.forEach((g) => g.isGrouped && (newState[g.id] = !allCollapsed));
              setCollapsedGroups(newState);
            }}
          >
            {Object.values(collapsedGroups).every(Boolean) ? (
              <>
                <KeyboardArrowDown fontSize='large' />
                <span className={styles.expandcollupselable}>{t('expand_all')}</span>
              </>
            ) : (
              <>
                <KeyboardArrowUp fontSize='large' />
                <span className={styles.expandcollupselable}>{t('collapse_all')}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {filteredData.length > 0 ? (
        <div className={styles.attrlist}>
          {filteredData.map((group) => {
            const showGroup = group.isGrouped;
            const children = group.visibleChildren;

            return (
              <div key={group.id}>
                {showGroup ? (
                  <div className={styles.row}>
                    <button
                      className={styles.expandIcon}
                      onClick={() =>
                        setCollapsedGroups((prev) => ({
                          ...prev,
                          [group.id]: !prev[group.id],
                        }))
                      }
                    >
                      {collapsedGroups[group.id] ? (
                        <KeyboardArrowDown fontSize='large' />
                      ) : (
                        <KeyboardArrowUp fontSize='large' />
                      )}
                    </button>
                    <Checkbox
                      size='medium'
                      checked={group.children.every((c) => selected[c.attrInternalName])}
                      indeterminate={
                        group.children.some((c) => selected[c.attrInternalName]) &&
                        !group.children.every((c) => selected[c.attrInternalName])
                      }
                      onChange={handleParentToggle(group)}
                      label={group.label}
                    />
                    <span className={styles.badge}>
                      {group.children.filter((c) => selected[c.attrInternalName]).length}/
                      {group.children.length}
                    </span>
                  </div>
                ) : null}
                {!showGroup || !collapsedGroups[group.id] ? (
                  <div style={{ marginLeft: showGroup ? '55px' : '31px' }}>
                    {children.map((child) => (
                      <div key={child.attrInternalName} className={styles.row}>
                        <span className={styles.emptySpacer} />
                        <Checkbox
                          size='medium'
                          key={child.attrInternalName}
                          label={
                            language === 'de' ? child.attrDisplayNamede : child.attrDisplayNameen
                          }
                          disabled={
                            child.attrInternalName ===
                              tableDetails?.mbdDataGrid.dataGridUniqueIdentifier &&
                            child.attrInternalName !== 'id1'
                          }
                          checked={
                            (child.attrInternalName ===
                              tableDetails?.mbdDataGrid.dataGridUniqueIdentifier &&
                              child.attrInternalName !== 'id1') ||
                            !!selected[child.attrInternalName]
                          }
                          onChange={handleChildToggle(child, group)}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.noresult}>{t('no_result')}</div>
      )}
    </div>
  );
};
